import React from 'react';
import { motion } from 'framer-motion';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { 
  TrendingUp, Eye, Users, BarChart3, Coffee,
  BrainCircuit, ClipboardList, Mic, CalendarCheck,
  ArrowRight, Target, Sparkles
} from 'lucide-react';

const personas = [
  {
    id: 'cro',
    label: "CRO / VP Sales",
    icon: TrendingUp,
    headline: "Forecast with confidence. Lead with evidence.",
    benefits: [
      { icon: BarChart3, title: "Evidence-Based Forecasts", desc: "AI-scored deal confidence replaces gut-feel pipeline reviews." },
      { icon: Eye, title: "Full Pipeline Visibility", desc: "Real-time deal health signals surface risks before they become surprises." },
      { icon: Users, title: "Team Performance at a Glance", desc: "Rep activity rankings, win rates, and coaching opportunities in one view." },
      { icon: Coffee, title: "Morning Briefing", desc: "Start every day knowing exactly what needs your attention and why." },
    ],
  },
  {
    id: 'manager',
    label: "Sales Manager",
    icon: ClipboardList,
    headline: "Coach your reps. Inspect every deal. Act on insights.",
    benefits: [
      { icon: BrainCircuit, title: "AI-Powered Coaching", desc: "Personalized coaching insights based on win/loss patterns for each rep." },
      { icon: Target, title: "Deal Inspection", desc: "See every signal, meeting note, and stakeholder interaction per deal." },
      { icon: Users, title: "Activity Tracking", desc: "Know who's working deals and who needs a nudge — without micromanaging." },
      { icon: ClipboardList, title: "Action Items", desc: "Assign and track follow-ups to resolution. Nothing falls through the cracks." },
    ],
  },
  {
    id: 'rep',
    label: "Sales Rep",
    icon: Mic,
    headline: "Zero admin. Just sell.",
    benefits: [
      { icon: Mic, title: "Zero CRM Admin", desc: "Meetings, emails, and calls auto-update your CRM. You never type in HubSpot again." },
      { icon: CalendarCheck, title: "Meeting Prep in Seconds", desc: "AI-generated briefs with deal context, stakeholder map, and talking points." },
      { icon: BrainCircuit, title: "AI Coach in Your Ear", desc: "Real-time support during meetings. Objection handling. Competitive intel." },
      { icon: Sparkles, title: "Auto Follow-Ups", desc: "Post-meeting summaries and draft follow-up emails generated automatically." },
    ],
  },
];

interface PersonaTabsProps {
  onScrollToDemo: () => void;
}

export const PersonaTabs = ({ onScrollToDemo }: PersonaTabsProps) => {
  return (
    <section className="w-full py-20 md:py-32 bg-muted/30">
      <div className="container px-4 md:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
            Built for <span className="text-gradient-teal">your role</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're running the number, managing a team, or closing deals — NoQ5 works for you.
          </p>
        </motion.div>

        <Tabs defaultValue="cro" className="max-w-5xl mx-auto">
          <TabsList className="w-full flex justify-center bg-card border border-border rounded-2xl p-1.5 h-auto gap-1 mb-10">
            {personas.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                className="flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-button transition-all"
              >
                <p.icon className="w-4 h-4" />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {personas.map((p) => (
            <TabsContent key={p.id} value={p.id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
                  {p.headline}
                </h3>

                <div className="grid sm:grid-cols-2 gap-6">
                  {p.benefits.map((b, i) => (
                    <div
                      key={i}
                      className="bg-card rounded-2xl p-6 border border-border hover:border-primary/30 hover:shadow-card transition-all duration-300"
                    >
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                        <b.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h4 className="font-semibold text-foreground mb-2">{b.title}</h4>
                      <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
                    </div>
                  ))}
                </div>

                <div className="flex justify-center mt-10">
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-xl shadow-button hover:shadow-glow transition-all duration-300 group"
                    onClick={onScrollToDemo}
                  >
                    Book a Demo
                    <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </motion.div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>
  );
};
